import { useCallback } from 'react'

import { Circle, HStack } from '@chakra-ui/react'
import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { DepthLabelVisibility } from '@editor/interfaces/editorSettings'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { PlaneSide } from '@editor/interfaces/shape'
import { getDepthTypeColor } from '@editor/services/DepthEstimation'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'

/**
 * The 最小 / 最大 / 平均 rows a side exposes, filtered by the かぶり厚 display
 * settings. A row turned off in settings is excluded entirely — it never
 * appears in the panel, so it can't be re-shown by hover or its toggle
 * (hatsuly-app issue #2646). A missing flag defaults to shown (older configs).
 */
const getElementRows = (
  itemId: string | undefined,
  keyPrefix: string,
  show: DepthLabelVisibility | undefined,
): EditorPanelItem[] =>
  (
    [
      { suffix: 'min', name: '最小' },
      { suffix: 'max', name: '最大' },
      { suffix: 'avg', name: '平均' },
    ] as const
  )
    .filter(({ suffix }) => show?.[suffix] !== false)
    .map(({ suffix, name }) => ({
      id: `${itemId}-${suffix}`,
      key: `${keyPrefix}-${itemId}-${suffix}`,
      name,
      onDelete: false,
      toggleVisibility: true,
    }))

const useElementsPanel = (): ElementsPanelConfig => {
  // Store
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const permissionSet = useSelector((state: RootState) => state.editor.permissionSet)
  const userType = useSelector((state: RootState) => state.user.userType)
  const depthDisplay = useSelector((state: RootState) => state.editorSettings.display.depth)

  // Permission check
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  return {
    /**
     * Return panel items as child of volume with 2 fixed items;
     * 1 for upper plane to cylinder, and 1 for lower plane to cylinder.
     */
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item']): EditorPanelItem[] => {
        // Depth falls under a volume
        if (!parent || !('item_type' in parent) || parent.item_type !== InspectionItemType.Volume) return []
        const prnt = parent as InspectionItem

        const items: EditorPanelItem[] = []
        const upper = inspectionItems.find(
          (item) =>
            item.volume_id === prnt.inspection_item_id &&
            item.item_type === InspectionItemType.PlaneToCylindersDistance &&
            item.plane_side === PlaneSide.UPPER,
        )
        if (upper) {
          items.push({
            key: `panel-depth-upper-${upper.inspection_item_id}`,
            name: '表かぶり厚',
            displayName: (
              <HStack as="span" display="inline-flex">
                <span>表かぶり厚</span>
                <Circle as="span" size="1em" bg={getDepthTypeColor(1)} />
              </HStack>
            ),
            item: upper,
            isVirtualContainer: true,
            onDelete: false,
            children: getElementRows(upper.inspection_item_id, 'panel-depth-upper', depthDisplay.upper.show),
          })
        }

        const lower = inspectionItems.find(
          (item) =>
            item.volume_id === prnt.inspection_item_id &&
            item.item_type === InspectionItemType.PlaneToCylindersDistance &&
            item.plane_side === PlaneSide.LOWER,
        )
        if (lower) {
          items.push({
            key: `panel-depth-lower-${lower.inspection_item_id}`,
            name: '裏かぶり厚',
            displayName: (
              <HStack as="span" display="inline-flex">
                <span>裏かぶり厚</span>
                <Circle as="span" size="1em" bg={getDepthTypeColor(2)} />
              </HStack>
            ),
            item: lower,
            isVirtualContainer: true,
            onDelete: false,
            children: getElementRows(lower.inspection_item_id, 'panel-depth-lower', depthDisplay.lower.show),
          })
        }

        if (items.some((item) => item.children && item.children.length > 0)) {
          return [
            {
              key: `panel-depth-${prnt.inspection_item_id}`,
              name: '表示',
              children: items,
              isVirtualContainer: true,
              onDelete: isAllowedToModify,
            },
          ]
        }

        return []
      },
      [inspectionItems, isAllowedToModify, depthDisplay],
    ),
  }
}

export default useElementsPanel
